import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STORE_KEY = "lifeos_music_v2";
const MusicCtx = createContext(null);
export const useMusic = () => useContext(MusicCtx);

const loadYT = () =>
  new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(window.YT); };
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api"; s.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(s);
    }
  });

const readStore = () => { try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch { return {}; } };

export const MusicProvider = ({ children }) => {
  const saved = useRef(readStore());
  const player = useRef(null);
  const ytRef = useRef(null);
  const hostElRef = useRef(null);
  const createdRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [current, setCurrent] = useState(saved.current.video || null);
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [volume, setVolume] = useState(typeof saved.current.volume === "number" ? saved.current.volume : 55);
  const [muted, setMuted] = useState(!!saved.current.muted);

  const currentRef = useRef(current); currentRef.current = current;
  const playingRef = useRef(playing); playingRef.current = playing;
  const readyRef = useRef(ready); readyRef.current = ready;
  const cityActiveRef = useRef(false);
  const resumedRef = useRef(false);

  const persist = useCallback((patch) => {
    const next = { ...readStore(), ...patch };
    saved.current = next;
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  }, []);

  const ensurePlayer = useCallback(() => {
    if (createdRef.current || !ytRef.current || !hostElRef.current) return;
    createdRef.current = true;
    const inner = document.createElement("div");
    inner.style.width = "100%"; inner.style.height = "100%";
    hostElRef.current.appendChild(inner);
    player.current = new ytRef.current.Player(inner, {
      host: "https://www.youtube.com",
      width: "100%", height: "100%",
      videoId: saved.current.video?.id || "",
      playerVars: { autoplay: 0, controls: 1, rel: 0, modestbranding: 1, playsinline: 1, iv_load_policy: 3 },
      events: {
        onReady: (e) => {
          e.target.setVolume(saved.current.muted ? 0 : (typeof saved.current.volume === "number" ? saved.current.volume : 55));
          if (saved.current.muted) e.target.mute();
          if (saved.current.video?.id) e.target.cueVideoById({ videoId: saved.current.video.id, startSeconds: saved.current.position || 0 });
          setReady(true);
        },
        onStateChange: (e) => {
          if (e.data === 1) { setPlaying(true); setBlocked(false); }
          else if (e.data === 2 || e.data === 0) setPlaying(false);
        },
      },
    });
  }, []);

  useEffect(() => { loadYT().then((YT) => { ytRef.current = YT; ensurePlayer(); }); }, [ensurePlayer]);

  const setHostEl = useCallback((el) => {
    if (!el || hostElRef.current === el) return;
    hostElRef.current = el;
    ensurePlayer();
  }, [ensurePlayer]);

  // Persist playback position while playing.
  useEffect(() => {
    const t = setInterval(() => {
      if (player.current && playingRef.current && currentRef.current) {
        try { persist({ position: Math.floor(player.current.getCurrentTime() || 0) }); } catch {}
      }
    }, 4000);
    return () => clearInterval(t);
  }, [persist]);

  const attemptResume = useCallback(() => {
    if (!player.current) return;
    try { player.current.playVideo(); } catch {}
    setTimeout(() => {
      try {
        const st = player.current?.getPlayerState();
        if (st !== 1 && st !== 3) setBlocked(true);
      } catch { setBlocked(true); }
    }, 1500);
  }, []);

  const setCityActive = useCallback((v) => {
    cityActiveRef.current = v;
    if (v && readyRef.current && currentRef.current && !playingRef.current && !resumedRef.current) {
      resumedRef.current = true; attemptResume();
    }
  }, [attemptResume]);

  useEffect(() => {
    if (ready && cityActiveRef.current && current && !playing && !resumedRef.current) {
      resumedRef.current = true; attemptResume();
    }
  }, [ready]); // eslint-disable-line

  const search = useCallback(async (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setSearching(true);
    try { const data = await api.musicSearch(term); setResults(data.results || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed. Try again."); }
    finally { setSearching(false); }
  }, [query]);

  const playVideo = useCallback((video) => {
    if (!player.current) return;
    setCurrent(video); setBlocked(false);
    persist({ video, position: 0 });
    try { player.current.loadVideoById(video.id); } catch {}
  }, [persist]);

  const togglePlay = useCallback(() => {
    if (!player.current) return;
    setBlocked(false);
    try { playingRef.current ? player.current.pauseVideo() : player.current.playVideo(); } catch {}
  }, []);

  const changeVolume = useCallback((v) => {
    setVolume(v); persist({ volume: v });
    if (player.current) { player.current.setVolume(v); if (v > 0 && muted) { player.current.unMute(); setMuted(false); persist({ muted: false }); } }
  }, [muted, persist]);

  const toggleMute = useCallback(() => {
    if (!player.current) return;
    const next = !muted; setMuted(next); persist({ muted: next });
    next ? player.current.mute() : player.current.unMute();
  }, [muted, persist]);

  return (
    <MusicCtx.Provider value={{ ready, playing, blocked, current, results, query, setQuery, searching, volume, muted, search, playVideo, togglePlay, changeVolume, toggleMute, setCityActive, setHostEl }}>
      {children}
    </MusicCtx.Provider>
  );
};
